import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveIcon from "@mui/icons-material/FileDownloadOutlined";
import PublishIcon from "@mui/icons-material/LanguageRounded";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import DocumentDialogIcon from "@entrilia/shared/icons/DocumentDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../../api/adminApi";
import type { DocumentCollectionOperations, DocumentInfo } from "../../../../../../api/types/documentCollectionTypes";
import useClientContext from "../../../../../../context/useClientContext";
import FileAccessIcon from "@entrilia/shared/icons/FileAccessIcon";
import UnpublishIcon from "@entrilia/shared/icons/UnpublishIcon";
import DocumentCollectionContext from "../../DocumentCollectionContext";
import { getDocumentAccessOption } from "../documentAccessHelper";

interface Props {
  documents: DocumentInfo[];
}

const getDownloadUrl = withErrorHandling(adminApi.getDownloadUrlForSelectedDocuments);

const MultiDocumentPreview = ({ documents }: Props) => {
  const { clientCode } = useClientContext();
  const {
    documentCollection,
    hasPermissionsToManage,
    investorPortalEnabled,
    onChangeDocumentsStatus,
    onChangeDocumentsAccess,
  } = useDefinedContext(DocumentCollectionContext);

  const [downloading, setDownloading] = useState(false);

  const handleDownloadClick = async () => {
    const documentIdentifiers = documents.map((doc) => ({
      id: doc.id,
      postMessageRequestId: doc.postMessageRequestId,
    }));

    setDownloading(true);
    const [url, error] = await getDownloadUrl(documentIdentifiers, clientCode);
    setDownloading(false);

    if (error) {
      logError(error, "[MultiDocumentPreview]");
      return;
    }

    await downloadFileFromUrl(url);
  };

  const handleChangeStatus = (operation: DocumentCollectionOperations) => () => {
    const postMessageRequestIds = documents.map((doc) => doc.postMessageRequestId);
    onChangeDocumentsStatus(operation, postMessageRequestIds, `${documents.length} documents`);
  };

  const handleChangeAccess = () => {
    const currentDocumentsAccess = distinct(documents.map((doc) => getDocumentAccessOption(doc.accessOptions)));

    onChangeDocumentsAccess(
      documents.map(({ id, postMessageRequestId }) => ({ id, postMessageRequestId })),
      currentDocumentsAccess
    );
  };

  const isPublishToLiveDisabled = documents.some((doc) => doc.publishingStatus === "Live");
  const isPublishToPreviewDisabled = documents.some((doc) => doc.publishingStatus === "Preview");

  const canChangeAccess = hasPermissionsToManage && investorPortalEnabled;
  const canChangeStatus = hasPermissionsToManage && documentCollection.messageSource === "DataImport";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexGrow: 1,
        width: "100%",
        height: "100%",
      }}
    >
      <Stack spacing={2} sx={{ alignItems: "center" }}>
        <DocumentDialogIcon sx={{ width: 80, height: 80 }} />
        <Typography color="textSecondary">{`Selected ${documents.length} documents`}</Typography>
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Button
            loading={downloading}
            color="secondary"
            variant="outlined"
            onClick={handleDownloadClick}
            startIcon={<SaveIcon />}
          >
            Download
          </Button>

          {canChangeAccess && (
            <Button color="secondary" variant="outlined" onClick={handleChangeAccess} startIcon={<FileAccessIcon />}>
              File Access
            </Button>
          )}

          {canChangeStatus && (
            <Button
              color="secondary"
              variant="outlined"
              onClick={handleChangeStatus("MakeLive")}
              startIcon={<PublishIcon />}
              disabled={isPublishToLiveDisabled}
            >
              Publish to Live
            </Button>
          )}

          {canChangeStatus && (
            <Button
              color="secondary"
              variant="outlined"
              onClick={handleChangeStatus("MakePreview")}
              startIcon={<UnpublishIcon />}
              disabled={isPublishToPreviewDisabled}
            >
              Unpublish to Preview
            </Button>
          )}

          {canChangeStatus && (
            <Button color="error" variant="outlined" onClick={handleChangeStatus("Revoke")} startIcon={<DeleteIcon />}>
              Delete
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default MultiDocumentPreview;
